import { isXmlMediaType } from '@scalar/helpers/http/is-xml-media-type';
import { isObject } from '@scalar/helpers/object/is-object';
import { getRaw } from '@scalar/json-magic/magic-proxy';
import { describe, inlineCode, item, link, list, paragraph, strong, text } from '@scalar/openapi-to-markdown/markdown';
import { getXmlBodyExample } from '@scalar/workspace-store/request-example';
import { object, resolve, string } from './document.js';
import { isJsonSchemaFormat } from './schema-format.js';
import { createSchemaView } from './schema-view.js';
/** Serialize authored data without adding workspace reference metadata or interpreting literal $ref keys. */
export const code = (value, lang = 'json') => ({
    type: 'code',
    lang,
    value: lang !== 'json' && typeof value === 'string' ? value : (JSON.stringify(getRaw(value), null, 2) ?? 'null'),
});
const annotations = [
    'title',
    '$id',
    '$schema',
    'contentEncoding',
    'contentMediaType',
    'format',
    'enum',
    'const',
    'default',
    'readOnly',
    'writeOnly',
    'deprecated',
    'minimum',
    'maximum',
    'exclusiveMinimum',
    'exclusiveMaximum',
    'multipleOf',
    'minLength',
    'maxLength',
    'pattern',
    'minItems',
    'maxItems',
    'uniqueItems',
    'minProperties',
    'maxProperties',
];
/** Infer the JSON type of an enum or constant without changing its constraints. */
const literalType = (value) => value === null
    ? 'null'
    : Array.isArray(value)
        ? 'array'
        : typeof value === 'number' && Number.isInteger(value)
            ? 'integer'
            : typeof value;
/** Render AsyncAPI / Draft 7 schemas without coercing them through OpenAPI's schema dialect. */
export const createSchemaRenderer = (reference = () => undefined, view = createSchemaView(), preserveReferences = false) => {
    const state = { remaining: 5000 };
    const render = (input, ancestors, depth, expand = false, mediaType = 'application/json') => {
        const linked = expand ? undefined : reference(input, depth, mediaType);
        if (linked) {
            return linked;
        }
        const resolved = resolve(input);
        if (typeof resolved === 'boolean') {
            return [paragraph(text(resolved ? 'Any value is allowed.' : 'No value is allowed.'))];
        }
        if (!isObject(resolved)) {
            return [code(resolved)];
        }
        const raw = object(resolved);
        const hasReferenceBranch = ['allOf', 'anyOf', 'oneOf'].some((keyword) => Array.isArray(raw[keyword]) &&
            raw[keyword].some((branch) => isObject(branch) && typeof branch.$ref === 'string'));
        const schema = preserveReferences && hasReferenceBranch ? raw : view(raw);
        if ('schema' in schema) {
            const format = string(schema.schemaFormat);
            if (isJsonSchemaFormat(format)) {
                return render(schema.schema, ancestors, depth, expand, mediaType);
            }
            return [
                paragraph(strong(text('Schema format:')), text(' '), inlineCode(format ?? '')),
                code(schema.schema, typeof schema.schema === 'string' ? 'text' : 'json'),
            ];
        }
        const identity = getRaw(resolved);
        if (ancestors.has(identity)) {
            const reference = isObject(input) ? string(input.$ref) : undefined;
            return [paragraph(text('Recursive schema'), ...(reference ? [text(': '), inlineCode(reference)] : []))];
        }
        if (depth >= 10 || state.remaining-- <= 0) {
            return [paragraph(text('Schema expansion limit reached.'))];
        }
        const next = new Set(ancestors).add(identity);
        const inferred = schema.const !== undefined
            ? literalType(schema.const)
            : Array.isArray(schema.enum) && schema.enum.length
                ? [...new Set(schema.enum.map(literalType))].join(' | ')
                : schema.properties || isObject(schema.additionalProperties)
                    ? 'object'
                    : schema.items !== undefined
                        ? 'array'
                        : undefined;
        const type = Array.isArray(schema.type) ? schema.type.join(' | ') : (string(schema.type) ?? inferred);
        const summary = type ? [inlineCode(type)] : [];
        for (const name of annotations) {
            if (schema[name] !== undefined) {
                summary.push(text(`${summary.length ? ', ' : ''}${name}: `), inlineCode(typeof schema[name] === 'string' ? schema[name] : JSON.stringify(getRaw(schema[name]))));
            }
        }
        const blocks = [
            ...(summary.length ? [paragraph(...summary)] : []),
            ...renderSchemaDocumentation(schema),
        ];
        const discriminator = schema.discriminator;
        const propertyName = typeof discriminator === 'string' ? discriminator : string(object(discriminator).propertyName);
        if (propertyName) {
            blocks.push(paragraph(strong(text('Discriminator:')), text(' '), inlineCode(propertyName)));
            const mappings = Object.entries(object(object(discriminator).mapping));
            if (mappings.length) {
                blocks.push(list(mappings.map(([name, target]) => item(paragraph(inlineCode(name), text(': '), inlineCode(String(target)))))));
            }
        }
        const required = new Set(Array.isArray(schema.required) ? schema.required : []);
        const unlistedRequired = [...required].filter((name) => typeof name === 'string' && !Object.hasOwn(object(schema.properties), name));
        if (unlistedRequired.length) {
            blocks.push(paragraph(strong(text('Required properties:')), text(' '), inlineCode(unlistedRequired.join(', '))));
        }
        const properties = Object.entries(object(schema.properties)).map(([name, property]) => {
            const children = render(property, next, depth + 1);
            const first = children[0];
            const compact = first?.type === 'paragraph' && ['inlineCode', 'link'].includes(first.children[0]?.type ?? '');
            return item(paragraph(strong(inlineCode(name)), ...(compact ? [text(': '), ...first.children] : []), ...(required.has(name) ? [text(' (required)')] : [])), ...(compact ? children.slice(1) : children));
        });
        if (properties.length) {
            blocks.push(list(properties));
        }
        for (const keyword of [
            'items',
            'additionalItems',
            'additionalProperties',
            'contains',
            'propertyNames',
            'not',
            'if',
            'then',
            'else',
        ]) {
            if (schema[keyword] !== undefined) {
                const values = Array.isArray(schema[keyword]) ? schema[keyword] : [schema[keyword]];
                blocks.push(paragraph(strong(text(`${keyword}:`))));
                for (const value of values) {
                    blocks.push(...render(value, next, depth + 1));
                }
            }
        }
        for (const keyword of ['oneOf', 'anyOf', 'allOf']) {
            if (Array.isArray(schema[keyword])) {
                blocks.push(paragraph(strong(text(`${keyword}:`))), list(schema[keyword].map((branch) => item(...render(branch, next, depth + 1)))));
            }
        }
        for (const keyword of ['patternProperties', 'dependencies']) {
            const entries = Object.entries(object(schema[keyword]));
            if (entries.length) {
                blocks.push(paragraph(strong(text(`${keyword}:`))), list(entries.map(([name, value]) => item(paragraph(inlineCode(name)), ...(Array.isArray(value) ? [code(value)] : render(value, next, depth + 1))))));
            }
        }
        blocks.push(...renderAuthoredSchemaExamples(schema, mediaType));
        return blocks.length ? blocks : [paragraph(text('Any value is allowed.'))];
    };
    return (input, expand = false, mediaType = 'application/json') => {
        state.remaining = 5000;
        return render(input, new Set(), 0, expand, mediaType);
    };
};
/** Authored examples remain at every usage, including when the schema itself becomes a link. */
export const renderAuthoredSchemaExamples = (input, mediaType = 'application/json') => {
    const resolved = object(input);
    const schema = 'schema' in resolved && isJsonSchemaFormat(resolved.schemaFormat) ? object(resolved.schema) : resolved;
    const examples = Array.isArray(schema.examples)
        ? schema.examples
        : schema.example === undefined
            ? []
            : [schema.example];
    return examples.flatMap((example) => {
        const label = paragraph(strong(text('Example:')));
        if (!isXmlMediaType(mediaType)) {
            return [label, code(example)];
        }
        if (typeof example === 'string') {
            return [label, code(example, 'xml')];
        }
        const result = getXmlBodyExample(schema, { value: example });
        return [
            label,
            ...(result.xml !== undefined
                ? [code(result.xml, 'xml')]
                : [paragraph(text('Unable to generate an XML example.'))]),
        ];
    });
};
/** Preserve schema documentation at both expanded definitions and linked usages. */
export const renderSchemaDocumentation = (input) => {
    const schema = object(input);
    const external = object(schema.externalDocs);
    return [
        ...describe(schema.description),
        ...(typeof external.url === 'string'
            ? [paragraph(link(external.url, string(external.description) || 'External documentation'))]
            : []),
    ];
};
